import type { PageRow, SortDir, WidgetCapabilities, WidgetSpec } from '@developerz/domain';
import { NO_WIDGET_CAPABILITIES } from '@developerz/domain';
import { batch, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  type DashboardWidgetView,
  useWidgetPage,
  type WidgetPageControls,
} from '../lib/dashboards';
import { usePagedQuery } from '../lib/paged';
import { LoadMore } from './LoadMore';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';
import { WidgetControls } from './WidgetControls';
import { ChartWidget } from './widgets/ChartWidget';
import { ListWidget } from './widgets/ListWidget';
import { StatWidget } from './widgets/StatWidget';
import { TableWidget } from './widgets/TableWidget';
import { TimelineWidget } from './widgets/TimelineWidget';

// Container for a DATA widget (arc 101 slice 09, task #81; controls #977) — pages
// the capability-gated pager through `usePagedQuery` and dispatches rows to the
// kind presenter. The controls the widget DECLARES are the controls it gets:
// sort/filter render only for declared keys, "Load more" only when it paginates —
// a zero-capability widget renders exactly as before (no toolbar, `{}` body).
// The switch narrows the discriminated `WidgetSpec`, so adding a kind is a new
// branch (OCP), never a cast. `markdown` never reaches here (no query).
function renderData(spec: WidgetSpec, rows: readonly PageRow[]): JSX.Element {
  switch (spec.kind) {
    case 'table':
      return <TableWidget spec={spec} rows={rows} />;
    case 'chart':
      return <ChartWidget spec={spec} rows={rows} />;
    case 'stat':
      return <StatWidget spec={spec} rows={rows} />;
    case 'list':
      return <ListWidget spec={spec} rows={rows} />;
    case 'timeline':
      return <TimelineWidget spec={spec} rows={rows} />;
    default:
      return null;
  }
}

export function DataWidget(props: {
  dashboardId: string;
  widget: DashboardWidgetView;
  token: string | null;
}): JSX.Element {
  const caps = (): WidgetCapabilities =>
    'capabilities' in props.widget.spec ? props.widget.spec.capabilities : NO_WIDGET_CAPABILITIES;
  const [sortKey, setSortKey] = createSignal('');
  const [sortDir, setSortDir] = createSignal<SortDir>('asc');
  const [filterKey, setFilterKey] = createSignal('');
  const [filterValue, setFilterValue] = createSignal('');
  const controls = (): WidgetPageControls => ({
    sort: sortKey() === '' ? null : { key: sortKey(), dir: sortDir() },
    filter:
      filterKey() === '' || filterValue() === ''
        ? null
        : { key: filterKey(), op: 'eq', values: [filterValue()] },
  });
  const list = usePagedQuery<PageRow>((cursor) =>
    useWidgetPage(props.dashboardId, props.widget.id, controls, cursor, props.token),
  );

  // Reset in the SAME batch as the control write (the RepoIssuesTab pattern): the
  // query key jumps straight to the new grammar's page one, and the accumulated
  // pages — sliced under the OLD sort/filter — are dropped with it.
  const apply = <T,>(set: (v: T) => void, v: T): void =>
    batch(() => {
      set(v);
      list.reset();
    });

  return (
    <>
      <Show when={caps().sort.length > 0 || caps().filter.length > 0}>
        <WidgetControls
          capabilities={caps()}
          sortKey={sortKey()}
          sortDir={sortDir()}
          filterKey={filterKey()}
          filterValue={filterValue()}
          onSortKey={(v) => apply(setSortKey, v)}
          onSortDir={(v) => apply(setSortDir, v)}
          // Switching the key clears the old value in the same batch: keeping it
          // would re-query `{key: newKey, values: [oldValue]}` — a pair the user
          // never asked for and maybe invalid for the new key.
          onFilterKey={(v) =>
            batch(() => {
              setFilterKey(v);
              setFilterValue('');
              list.reset();
            })
          }
          onFilterValue={(v) => apply(setFilterValue, v)}
        />
      </Show>
      <QueryBoundary
        query={list.view}
        // Without this the `hasData` gate is true for any defined data, so a
        // zero-row page renders `renderData([])` and the empty message is dead
        // code. Same wiring as CiView.
        isEmpty={(rows) => rows.length === 0}
        loading={<Skeleton height="6rem" />}
        empty={<p class="widget__empty">{t('widgets.empty')}</p>}
      >
        {(rows) => (
          <>
            {renderData(props.widget.spec, rows)}
            <Show when={caps().paginate}>
              <LoadMore list={list} />
            </Show>
          </>
        )}
      </QueryBoundary>
    </>
  );
}
